import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { FiSearch, FiMusic, FiArrowUpRight } from "react-icons/fi";

import productsService from "../../api/services/products";
import { selectBandsDropdown } from "../../store/slices/bands/slice";
import { formatPrice } from "../../utils/utils";
import { useScrollLock } from "../../hooks/useScrollLock";

/*
 * Globalna pretraga (command-palette), pretražuje proizvode (API)
 * i bendove (već učitan dropdown u store-u). Otvara se iz header-a.
 */

const MIN_QUERY = 2;

// Backend za listu proizvoda ume da vrati i grupisan format po kategorijama
const flattenProducts = (data) => {
  if (!Array.isArray(data)) return [];
  if (data[0]?.categoryName && data[0]?.products) {
    return data.flatMap((category) =>
      Array.isArray(category.products) ? category.products : [],
    );
  }
  return data;
};

const SearchOverlay = ({ open, onClose }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);

  const { data: bandsData } = useSelector(selectBandsDropdown);

  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useScrollLock(open);

  // Fokus + reset pri otvaranju
  useEffect(() => {
    if (open) {
      setQuery("");
      setProducts([]);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // ESC zatvara
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Debounce pretraga proizvoda
  useEffect(() => {
    if (!open) return undefined;
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      setProducts([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      productsService
        .getProducts({ page: 1, limit: 6, search: trimmed })
        .then((response) => {
          if (requestId !== requestIdRef.current) return; // stigao noviji upit
          setProducts(flattenProducts(response?.data).slice(0, 6));
          setLoading(false);
        })
        .catch(() => {
          if (requestId !== requestIdRef.current) return;
          setProducts([]);
          setLoading(false);
        });
    }, 300);

    return () => clearTimeout(timer);
  }, [query, open]);

  const trimmed = query.trim();
  const matchedBands =
    trimmed.length >= MIN_QUERY
      ? (bandsData || [])
          .filter((band) =>
            band.name?.toLowerCase().includes(trimmed.toLowerCase()),
          )
          .slice(0, 5)
      : [];

  const goTo = (path) => {
    onClose();
    navigate(path);
    setTimeout(() => {
      window.scrollTo(0, 0);
    }, 300);
  };

  if (!open) return null;

  const hasResults = matchedBands.length > 0 || products.length > 0;

  return (
    <Backdrop onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <InputRow>
          <FiSearch size={18} />
          <SearchInput
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Pretraži proizvode i bendove..."
            aria-label="Pretraga"
          />
          <Kbd>ESC</Kbd>
        </InputRow>

        <Results>
          {trimmed.length < MIN_QUERY ? (
            <Hint>Ukucaj bar {MIN_QUERY} slova za pretragu.</Hint>
          ) : (
            <>
              {matchedBands.length > 0 && (
                <>
                  <GroupLabel>Bendovi</GroupLabel>
                  {matchedBands.map((band) => (
                    <ResultItem
                      key={`band-${band.id}`}
                      onClick={() => goTo(`/bend/${band.slug || band.id}`)}
                    >
                      <BandIcon>
                        <FiMusic size={15} />
                      </BandIcon>
                      <ItemName>{band.name}</ItemName>
                      <FiArrowUpRight size={14} className="go" />
                    </ResultItem>
                  ))}
                </>
              )}

              {products.length > 0 && (
                <>
                  <GroupLabel>Proizvodi</GroupLabel>
                  {products.map((product) => (
                    <ResultItem
                      key={`product-${product.id}`}
                      onClick={() =>
                        goTo(`/proizvod/${product.slug || product.id}`)
                      }
                    >
                      <Thumb
                        src={product.mainImage || "/placeholder.jpg"}
                        alt=""
                        loading="lazy"
                      />
                      <ItemBody>
                        <ItemName>{product.name}</ItemName>
                        <ItemMeta>
                          {product.band?.name || product.festival?.name || ""}
                        </ItemMeta>
                      </ItemBody>
                      <Price>
                        {formatPrice(product.salePrice || product.price)}
                      </Price>
                    </ResultItem>
                  ))}
                </>
              )}

              {loading && !hasResults && <Hint>Tražim...</Hint>}
              {!loading && !hasResults && (
                <Hint>
                  Nema rezultata za "<strong>{trimmed}</strong>".
                </Hint>
              )}
            </>
          )}
        </Results>
      </Panel>
    </Backdrop>
  );
};

export default SearchOverlay;

/* ------------------------------- stilovi ------------------------------- */

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  padding: 12vh 1rem 0;
`;

const Panel = styled.div`
  max-width: 620px;
  margin: 0 auto;
  background: var(--bg-surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-card);
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1.1rem;
  border-bottom: 1px solid var(--border);

  > svg {
    color: var(--brand);
    flex-shrink: 0;
  }
`;

const SearchInput = styled.input`
  flex: 1;
  height: 54px;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 1rem;
  font-family: var(--font-body);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    outline: none;
  }
`;

const Kbd = styled.kbd`
  padding: 0.2rem 0.45rem;
  border: 1px solid var(--border-strong);
  border-radius: 4px;
  color: var(--text-muted);
  font-size: 0.68rem;
  font-family: var(--font-body);
  letter-spacing: 0.05em;
`;

const Results = styled.div`
  max-height: 52vh;
  overflow-y: auto;
  padding: 0.5rem;
`;

const GroupLabel = styled.div`
  padding: 0.6rem 0.6rem 0.35rem;
  color: var(--text-muted);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
`;

const ResultItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.55rem 0.6rem;
  background: transparent;
  border: none;
  border-radius: var(--radius-control);
  color: var(--text-primary);
  text-align: left;
  cursor: pointer;
  transition: background 0.15s ease;

  .go {
    color: var(--text-muted);
    flex-shrink: 0;
    margin-left: auto;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);

    .go {
      color: var(--brand);
    }
  }
`;

const BandIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-control);
  background: var(--brand-dim);
  color: var(--brand);
  flex-shrink: 0;
`;

const Thumb = styled.img`
  width: 34px;
  height: 34px;
  border-radius: var(--radius-control);
  object-fit: cover;
  background: #ffffff;
  flex-shrink: 0;
`;

const ItemBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 0.92rem;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Price = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary);
  white-space: nowrap;
  flex-shrink: 0;
`;

const Hint = styled.div`
  padding: 1.5rem 1rem;
  text-align: center;
  color: var(--text-muted);
  font-size: 0.88rem;

  strong {
    color: var(--text-secondary);
  }
`;
